import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { bannersApi } from '../api/banners.api';
import type { CreateBannerDto, UpdateBannerDto, SetActiveDto, CreateStoryDto, AttachLinkDto } from '../types';

export function useBanners(includeStories: boolean = false, activeOnly: boolean = false) {
    return useQuery({
        queryKey: ['banners', includeStories, activeOnly],
        queryFn: () => bannersApi.getAll(includeStories, activeOnly),
    });
}

export function useBanner(id: number) {
    return useQuery({
        queryKey: ['banners', id],
        queryFn: () => bannersApi.getById(id),
        enabled: !!id,
    });
}

export function useCreateBanner() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ data, file, storyFiles }: { data: CreateBannerDto; file?: File; storyFiles?: File[] }) =>
            bannersApi.create(data, file, storyFiles),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['banners'] });
        },
    });
}

export function useUpdateBanner() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data, file, storyFiles, deletedStoryIds }: { id: number; data: UpdateBannerDto; file?: File; storyFiles?: File[]; deletedStoryIds?: number[] }) =>
            bannersApi.update(id, data, file, storyFiles, deletedStoryIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['banners'] });
        },
    });
}

export function useSetBannerActive() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: SetActiveDto }) =>
            bannersApi.setActive(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['banners'] });
        },
    });
}

export function useAttachLinkToBanner() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: AttachLinkDto }) =>
            bannersApi.attachLink(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['banners'] });
        },
    });
}

export function useDeleteBanner() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => bannersApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['banners'] });
        },
    });
}

export function useReorderBanners() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (items: { id: number; order: number }[]) => bannersApi.updateOrder(items),
        onSuccess: (data) => {
            queryClient.setQueryData(['banners', true, false] as const, data);
        },
        onError: () => {
            queryClient.invalidateQueries({ queryKey: ['banners'] });
            notifications.show({
                title: 'Ошибка',
                message: 'Не удалось изменить порядок баннеров',
                color: 'red',
            });
        },
    });
}

export function useCreateStory() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ data, file }: { data: CreateStoryDto; file?: File }) =>
            bannersApi.createStory(data, file),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['banners'] });
        },
    });
}

